/**
 * MemoryRecapGhostPill — the quiet "a keepsake is waiting" pill on
 * the Memory Wall.
 *
 * Per Craig's brief: "discoverable rather than announced". The pill
 * appears ONLY when:
 *   • a `?keepsake=<guest_id>` param is present in the wall URL
 *     (carried over from the recap email "Revisit the wall" link), AND
 *   • the backend confirms a recap was actually delivered to that
 *     guest_id (anti-spoofing — the param alone never triggers it)
 *
 * Visual rules:
 *   • dark glass + ~30% gold border + slow 4s respiration
 *   • italic Georgia serif, low contrast on first paint, lifts on hover
 *   • copy: "✦ A keepsake from this night is waiting for you"
 *   • auto-dismisses after first open (per-device via localStorage)
 *     so it never nags
 *   • taps → opens a soft modal with the recap iframe inline
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISMISS_KEY = (eventId, gid) => `eventdrop_keepsake_pill_${eventId}_${gid}`;

export default function MemoryRecapGhostPill({ eventId, guestId, onVisibilityChange }) {
  const [hasRecap, setHasRecap] = useState(false);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!eventId || !guestId) return;
    let cancelled = false;
    try {
      if (localStorage.getItem(DISMISS_KEY(eventId, guestId))) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/memory-recap/check`, {
          params: { guest_id: guestId },
        });
        if (!cancelled) setHasRecap(!!r.data?.has_recap);
      } catch {
        if (!cancelled) setHasRecap(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, guestId]);

  // Report visibility upward so peer pills (e.g. LatestChapterPill) can
  // suppress themselves — the wall must never read as a notification
  // centre, so we enforce at-most-one quiet pill at a time.
  useEffect(() => {
    if (onVisibilityChange) onVisibilityChange(hasRecap && !dismissed);
  }, [hasRecap, dismissed, onVisibilityChange]);

  const onOpen = () => {
    setOpen(true);
    // Mark as opened so the pill doesn't return next visit.
    try { localStorage.setItem(DISMISS_KEY(eventId, guestId), '1'); } catch { /* ignore */ }
  };

  const [pressed, setPressed] = useState(false);
  const press = () => setPressed(true);
  const release = () => setPressed(false);

  if (!hasRecap || dismissed) return null;

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        data-testid="memory-wall-keepsake-pill"
        data-pressed={pressed ? '1' : '0'}
        aria-label="A keepsake from this night is waiting for you"
        onPointerDown={press}
        onPointerUp={release}
        onPointerLeave={release}
        onPointerCancel={release}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '10px 18px',
          borderRadius: 999,
          background: 'rgba(20,12,6,0.65)',
          border: `1px solid ${pressed ? 'rgba(212,175,94,0.18)' : 'rgba(212,175,94,0.32)'}`,
          backdropFilter: 'blur(12px)',
          color: 'rgba(255,235,210,0.85)',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: 12.5,
          letterSpacing: '0.01em',
          cursor: 'pointer',
          boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
          animation: 'keepsakeBreathe 4.2s ease-in-out infinite',
          transform: pressed ? 'translateY(1px)' : 'translateY(0)',
          transition: 'transform 220ms ease, border-color 220ms ease',
        }}
      >
        <span aria-hidden style={{ fontSize: 13, color: '#D4AF5E' }}>✦</span>
        A keepsake from this night is waiting for you
      </button>

      {open && (
        <div
          data-testid="keepsake-letter-overlay"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(6,3,1,0.86)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
            animation: 'keepsakeFade 320ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(820px, 100%)',
              maxHeight: '90vh',
              background: '#1a1108',
              borderRadius: 16,
              border: '1px solid rgba(212,175,94,0.30)',
              boxShadow: '0 36px 96px rgba(0,0,0,0.6)',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{
              padding: '16px 22px',
              borderBottom: '1px solid rgba(212,175,94,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(180deg, rgba(40,28,14,0.55) 0%, rgba(20,12,6,0.55) 100%)',
            }}>
              <div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: '#A8854C', marginBottom: 4,
                }}>A preserved letter</div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontSize: 17, color: '#FFE7A8',
                }}>
                  From this night ✦
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="keepsake-close-btn"
                aria-label="Return to the wall"
                style={{
                  background: 'transparent', border: 'none', color: '#C9A87A',
                  fontSize: 22, cursor: 'pointer', padding: '4px 8px',
                }}
              >×</button>
            </div>
            <iframe
              data-testid="keepsake-letter-iframe"
              title="A letter from this night"
              src={`${API}/events/${eventId}/memory-recap/letter?guest_id=${encodeURIComponent(guestId)}`}
              sandbox=""
              style={{ flex: 1, width: '100%', height: '72vh', border: 0, background: '#FAF6EE' }}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes keepsakeBreathe {
          0%, 100% { box-shadow: 0 8px 22px rgba(0,0,0,0.4), 0 0 0 0 rgba(212,175,94,0.0); }
          50%      { box-shadow: 0 8px 22px rgba(0,0,0,0.4), 0 0 22px 1px rgba(212,175,94,0.18); }
        }
        @keyframes keepsakeFade {
          from { opacity: 0; } to { opacity: 1; }
        }
      `}</style>
    </>
  );
}
